import React, { useEffect, createContext, useContext, useState, useCallback } from 'react'
import { Modal, View, StyleSheet, TouchableOpacity, Platform } from 'react-native'
import { Feather } from '@expo/vector-icons'
// Eliminamos el uso del BottomSheet global dentro del Modal web para evitar layering detrás del overlay
import { BottomSheetBackAlert } from '../../modals/BottomSheetAlert'
import { getData } from '../../appBar/context/HeaderContext'
import Survey from './survey'

// Context para indicar que Survey está dentro de un modal web
export const SurveyModalContext = createContext({ isWebModal: false, closeModal: (_courseParams) => {} })

/**
 * Modal wrapper para Survey en web
 * Muestra el Survey como un modal con overlay oscuro
 * Maneja el cierre con confirmación igual que en mobile
 */
export default function SurveyModal({ route }) {
    const { setCurrentFooterRoute, prevFooterRoute, goBackInternal } = getData()
    const [visible, setVisible] = useState(true)
    const [showConfirm, setShowConfirm] = useState(false)

    useEffect(() => {
        console.log('[SurveyModal] Survey modal opened with params:', route.params)
        console.log('[SurveyModal] Evaluation param:', route.params?.evaluation)
    }, [])

    const handleClose = () => {
        console.log('[SurveyModal] Close button pressed, showing confirmation')
        // Mostrar confirmación local (sin BottomSheet) para que aparezca por encima del overlay del Modal
        setShowConfirm(true)
    }

    const handleConfirmClose = (shouldClose) => {
        console.log('--------------------- handleConfirmClose -------------------------')
        console.log('[SurveyModal] Close confirmation:', shouldClose)
        if (shouldClose) {
            // Navegar de vuelta a Course con los mismos params (mantener contexto del curso)
            console.log('[SurveyModal] Navigating back to Course')
            const courseParams = {
                course_id: route.params.course_id,
                courseItem: route.params.courseItem,
            }
            // Ocultar modal y luego redirigir a Course
            setVisible(false)

            console.log('prevFooterRoute => ', prevFooterRoute)

            if (prevFooterRoute === 'Course') {
                // Volver a la ruta previa sin duplicar Course en el stack
                try {
                    goBackInternal && goBackInternal()
                } catch {}
            } else {
                setCurrentFooterRoute('Course', courseParams)
            }
        }
        // Cerrar confirmación local
        setShowConfirm(false)
    }

    // Exponer un cierre directo para usar desde Survey (cuando termina y toca Cerrar)
    const closeModal = useCallback(
        (courseParams) => {
            try {
                setVisible(false)
                const params = courseParams ?? {
                    course_id: route.params.course_id,
                    courseItem: route.params.courseItem,
                }
                if (prevFooterRoute === 'Course') {
                    try {
                        goBackInternal && goBackInternal()
                    } catch {}
                } else {
                    setCurrentFooterRoute('Course', params)
                }
            } catch (e) {
                console.log('[SurveyModal] closeModal error:', e)
            }
        },
        [route?.params, setCurrentFooterRoute, prevFooterRoute, goBackInternal],
    )

    // Prevenir cierre al hacer click en el overlay (debe usar el botón X)
    const handleOverlayPress = () => {
        console.log('[SurveyModal] Overlay pressed, showing confirmation')
        handleClose()
    }

    return (
        <Modal visible={visible} transparent={true} animationType='fade' onRequestClose={handleClose}>
            <View style={styles.modalOverlay}>
                {/* Overlay oscuro - al hacer click muestra confirmación */}
                <TouchableOpacity style={styles.overlayTouchable} activeOpacity={1} onPress={handleOverlayPress} />

                {/* Contenedor del modal */}
                <View style={styles.modalContainer}>
                    {/* Botón de cerrar en la esquina superior derecha */}
                    <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
                        <Feather name='x' size={24} color='#fff' />
                    </TouchableOpacity>

                    {/* Survey component con contexto indicando que está en modal */}
                    <View style={styles.surveyContainer}>
                        {console.log('[SurveyModal] Rendering Survey component with route:', route)}
                        <SurveyModalContext.Provider value={{ isWebModal: true, closeModal }}>
                            <Survey route={route} />
                        </SurveyModalContext.Provider>
                    </View>

                    {/* Confirmación local (encima del contenido del modal) */}
                    {showConfirm && (
                        <View style={styles.confirmOverlay}>
                            <View style={styles.confirmCard}>
                                <BottomSheetBackAlert
                                    closeBottomSheet={(shouldClose) => {
                                        // Reutilizamos el mismo contrato: true cierra y navega; false solo cierra el confirm
                                        if (shouldClose) {
                                            handleConfirmClose(true)
                                        } else {
                                            setShowConfirm(false)
                                        }
                                    }}
                                />
                            </View>
                        </View>
                    )}
                </View>
            </View>
        </Modal>
    )
}

// Hook para usar en Survey.js
export const useSurveyModal = () => useContext(SurveyModalContext)

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    overlayTouchable: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    modalContainer: {
        width: '90%',
        maxWidth: 900,
        height: '93%',
        maxHeight: 850,
        backgroundColor: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        elevation: 10,
    },
    closeButton: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 1000,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        width: 40,
        height: 40,
        borderRadius: 20,
        justifyContent: 'center',
        alignItems: 'center',
    },
    surveyContainer: {
        flex: 1,
        width: '100%',
        height: '100%',
    },
    // Overlay interno para confirmación (por encima del contenido del modal)
    confirmOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
    },
    confirmCard: {
        width: '90%',
        maxWidth: 600,
        borderRadius: 16,
        backgroundColor: '#fff',
        overflow: 'hidden',
    },
})
